/**
 * CTRL_05 Plugin - Training Data Audit Trail
 * Custom UI with audit log viewer and data lineage tracking
 * Extends GenericConfigPanel with visual audit timeline
 */

import React, { useState, useEffect } from 'react';
import {
  Card,
  Timeline,
  Alert,
  Table,
  Tag,
  Space,
  Statistic,
  Row,
  Col,
} from 'antd';
import {
  FileTextOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import GenericConfigPanel from '../GenericConfigPanel';
import { colors } from '../../../../theme/designTokens';

/**
 * Training Data Audit Dashboard Component
 */
function TrainingDataAuditDashboard({ config, projectId }) {
  const [auditLogs, setAuditLogs] = useState([]);
  const [stats, setStats] = useState({
    totalRecords: 0,
    trackedChanges: 0,
    dataVersions: 0,
  });

  useEffect(() => {
    if (config?.enabled && projectId) {
      fetchAuditLogs();
    }
  }, [config, projectId]);

  const fetchAuditLogs = async () => {
    // Mock data - replace with actual API call
    setTimeout(() => {
      setStats({
        totalRecords: 15420,
        trackedChanges: 342,
        dataVersions: 12,
      });
      setAuditLogs([
        {
          id: 1,
          action: 'Data Added',
          dataset: 'training_set_v3',
          user: 'system',
          timestamp: new Date().toISOString(),
          records: 1500,
        },
        {
          id: 2,
          action: 'Data Modified',
          dataset: 'validation_set',
          user: 'admin',
          timestamp: new Date(Date.now() - 3600000).toISOString(),
          records: 250,
        },
      ]);
    }, 500);
  };

  const columns = [
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      render: (action) => {
        const color = action.includes('Added') ? 'green' : action.includes('Modified') ? 'blue' : 'orange';
        return <Tag color={color}>{action}</Tag>;
      },
    },
    {
      title: 'Dataset',
      dataIndex: 'dataset',
      key: 'dataset',
    },
    {
      title: 'User',
      dataIndex: 'user',
      key: 'user',
    },
    {
      title: 'Records',
      dataIndex: 'records',
      key: 'records',
      align: 'right',
    },
    {
      title: 'Timestamp',
      dataIndex: 'timestamp',
      key: 'timestamp',
      render: (ts) => new Date(ts).toLocaleString(),
    },
  ];

  if (!config?.enabled) {
    return (
      <Alert
        message="Training Data Audit Disabled"
        description="Enable auditing to track all changes to training datasets."
        type="info"
        showIcon
      />
    );
  }

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* Audit Statistics */}
      <Row gutter={16}>
        <Col span={8}>
          <Card size="small">
            <Statistic
              title="Total Records"
              value={stats.totalRecords}
              prefix={<FileTextOutlined />}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card size="small">
            <Statistic
              title="Tracked Changes"
              value={stats.trackedChanges}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: colors.primary[500] }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card size="small">
            <Statistic
              title="Data Versions"
              value={stats.dataVersions}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Audit Log Table */}
      <div>
        <h4>Recent Audit Activity</h4>
        <Table
          columns={columns}
          dataSource={auditLogs}
          rowKey="id"
          size="small"
          pagination={{ pageSize: 10 }}
        />
      </div>
    </Space>
  );
}

/**
 * CTRL_05 Plugin Component
 */
function CTRL_05_Plugin({ projectId, controlCode }) {
  const [config, setConfig] = useState({
    enabled: false,
    audit_granularity: 'detailed',
  });

  return (
    <div>
      {/* Audit Dashboard */}
      <TrainingDataAuditDashboard config={config} projectId={projectId} />

      {/* Generic Config Panel - usePlugin={false} prevents infinite loop */}
      <GenericConfigPanel projectId={projectId} controlCode={controlCode} usePlugin={false} />
    </div>
  );
}

export default CTRL_05_Plugin;
